import { type Routes } from '@angular/router';

import { authGuard } from './core/guards/auth.guard';
import { featureGuard } from './core/guards/feature.guard';
import { homeGuard, pageGuard } from './core/guards/page.guard';
import { chefGuard } from './core/guards/role.guard';
import { unsavedChangesGuard } from './core/guards/unsaved-changes.guard';

export const routes: Routes = [
  // The start page is the first page the user may open (page.guard.ts), not always the dashboard.
  { path: '', pathMatch: 'full', canActivate: [authGuard, homeGuard], children: [] },

  // Auth0's redirect_uri (see app.config.ts) — no canActivate of its own, it IS the
  // in-progress-login screen.
  {
    path: 'auth/callback',
    loadComponent: () => import('./features/auth-callback/auth-callback-page').then((m) => m.AuthCallbackPage),
  },

  {
    path: 'dashboard',
    canActivate: [authGuard, chefGuard, pageGuard('dashboard')],
    loadComponent: () => import('./features/dashboard/dashboard-page').then((m) => m.DashboardPage),
  },
  {
    path: 'employees',
    canActivate: [authGuard, chefGuard, pageGuard('employees')],
    loadComponent: () => import('./features/employees/employees-page').then((m) => m.EmployeesPage),
  },
  {
    path: 'employees/:id',
    canActivate: [authGuard, chefGuard, pageGuard('employees')],
    canDeactivate: [unsavedChangesGuard],
    loadComponent: () => import('./features/employees/employee-detail-page').then((m) => m.EmployeeDetailPage),
  },
  {
    path: 'time',
    canActivate: [authGuard, chefGuard, pageGuard('time')],
    loadComponent: () => import('./features/time/time-page').then((m) => m.TimePage),
  },
  {
    path: 'absences',
    canActivate: [authGuard, chefGuard, featureGuard('Absences'), pageGuard('absences')],
    loadComponent: () => import('./features/absences/absences-page').then((m) => m.AbsencesPage),
  },
  {
    path: 'shifts',
    canActivate: [authGuard, chefGuard, featureGuard('ShiftPlanning'), pageGuard('shifts')],
    loadComponent: () => import('./features/shifts/shifts-page').then((m) => m.ShiftsPage),
  },
  {
    path: 'shift-swaps',
    canActivate: [authGuard, chefGuard, featureGuard('ShiftSwap'), pageGuard('shiftSwaps')],
    loadComponent: () => import('./features/shift-swaps/shift-swaps-page').then((m) => m.ShiftSwapsPage),
  },
  {
    path: 'month-close',
    canActivate: [authGuard, chefGuard, pageGuard('monthClose')],
    loadComponent: () => import('./features/month-close/month-close-page').then((m) => m.MonthClosePage),
  },
  {
    path: 'exports',
    canActivate: [authGuard, chefGuard, pageGuard('exports')],
    loadComponent: () => import('./features/exports/exports-page').then((m) => m.ExportsPage),
  },
  {
    path: 'terminal-scans',
    canActivate: [authGuard, chefGuard, pageGuard('terminalScans')],
    loadComponent: () => import('./features/terminal-scans/terminal-scans-page').then((m) => m.TerminalScansPage),
  },
  {
    path: 'terminals',
    canActivate: [authGuard, chefGuard, pageGuard('terminals')],
    canDeactivate: [unsavedChangesGuard],
    loadComponent: () => import('./features/terminals/terminals-page').then((m) => m.TerminalsPage),
  },
  {
    path: 'settings',
    canActivate: [authGuard, chefGuard, pageGuard('settings')],
    canDeactivate: [unsavedChangesGuard],
    loadComponent: () => import('./features/settings/settings-page').then((m) => m.SettingsPage),
  },

  // Common failure landing page for auth.guard/role.guard/feature.guard — no canActivate of
  // its own, or a denied user could never reach the page that explains why.
  {
    path: 'access-denied',
    loadComponent: () => import('./features/access-denied/access-denied-page').then((m) => m.AccessDeniedPage),
  },

  { path: '**', redirectTo: '' },
];
